import { ViewIcon } from '@chakra-ui/icons';
import { Badge, Flex, IconButton, Text, Tooltip } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { get, isEmpty, toNumber } from 'lodash';
import React from 'react';

import { RawMaterialDataSourceType } from '@/app/factory-database/src/enums/raw-material';
import { RMTRouteType } from '@/app/factory-database/src/enums/rmt';
import { transferEmissionsDisplay } from '@/app/org-cfv/[projectId]/src/utils';
import ActivityDataConstants from '@/app/org-cfv/src/constants/ActivityDataConstants';
import { DataActivityStatus } from '@/app/org-cfv/src/enums/EmissionSourceEnum';
import { Approval } from '@/app/org-cfv/src/typings/ActivityDataDetailsTypings';
import { EmissionsType } from '@/app/org-cfv/src/typings/CreateActivityDataTypings';
import { HiOutlineCollectionIcon } from '@/components/common/Icons/HiOutlineCollectionIcon';
import { APP_DATE_FORMAT } from '@/constants/date';
import { NextTFunction, safeTranslate, useNextTranslation } from '@/i18n/client';
import { Document } from '@/typings/common';
import { formatDate } from '@/utils/common';
import { isValidDate } from '@/utils/date';
import { toThousandthString } from '@/utils/numbers';
import { isNumber } from '@/utils/validations';

const TooltipIcon: React.FC = () => {
  const { t } = useNextTranslation();
  return (
    <Tooltip hasArrow label={t('bulk-input.bulkInput')}>
      <HiOutlineCollectionIcon color='gray.300' w='1rem' h='1rem' />
    </Tooltip>
  );
};

export const getSerialNumberHeader = (t: NextTFunction, inDetails = false): ColumnDef<Approval & EmissionsType> => ({
  header: t('common.serialNumber'), //編號
  accessorFn: (row) => {
    const key = inDetails ? 'sourceTypeSerialNo' : 'listSerialNo';
    return get(row, `activityData.metadata.${key}`) || get(row, `metadata.${key}`, '');
  },
});

export const getDataEntrySerialNumberHeader = (
  t: NextTFunction,
  showBulkIcon: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  return {
    header: t('common.serialNumber'), //編號
    accessorKey: 'activityData.metadata.listSerialNo',
    cell: ({ row: { original }, getValue }) => {
      const isBulkInput = showBulkIcon && original.isBatch;
      const serialNumber = getValue() as string;
      return isBulkInput ? (
        <Flex alignItems='center' gap={2} data-testid={`bulk_input`}>
          {serialNumber}
          <TooltipIcon />
        </Flex>
      ) : (
        serialNumber
      );
    },
  };
};
const getDocumentDateText = (original: Approval & EmissionsType, isBulkPreview: boolean = false) => {
  const documentDate = isBulkPreview ? original.documentDate : original.activityData.documentDate;
  const documentEndDate = isBulkPreview ? original.documentEndDate : original.activityData.documentEndDate;

  return [
    isValidDate(documentDate) ? dayjs(documentDate).format(APP_DATE_FORMAT) : '',
    isValidDate(documentEndDate) ? dayjs(documentEndDate).format(APP_DATE_FORMAT) : '',
  ]
    .filter(Boolean)
    .join(' - ');
};

export const getDocumentDateDefaultHeader = (
  t: NextTFunction,
  {
    label,
    isBulkPreview = false,
  }: {
    label?: string;
    isBulkPreview?: boolean;
  },
): ColumnDef<Approval & EmissionsType> => {
  return {
    header: label || t('org-cfv-data-entry.docDate'), //單據日期（月 / 日 / 年）
    accessorKey: isBulkPreview ? 'documentDate' : 'activityData.documentDate',
    cell: ({ row: { original } }) => getDocumentDateText(original, isBulkPreview),
  };
};

export const getDatePeriodHeader = (
  t: NextTFunction,
  {
    label,
    isBulkPreview = false,
  }: {
    label?: string;
    isBulkPreview?: boolean;
  } = {},
): ColumnDef<Approval & EmissionsType> => {
  return {
    header: label || t('org-cfv-data-entry.date'), //日期（月 / 日 / 年）
    accessorKey: isBulkPreview ? 'documentDate' : 'activityData.documentDate',
    cell: ({ row: { original } }) => {
      const documentDate = isBulkPreview ? original.documentDate : original.activityData.documentDate;
      const documentEndDate = isBulkPreview ? original.documentEndDate : original.activityData.documentEndDate;

      return [formatDate(documentDate), formatDate(documentEndDate)].join(' - ');
    },
  };
};

export const getDocumentDatePeriodHeader = (
  t: NextTFunction,
  {
    label,
    isBulkPreview = false,
  }: {
    label?: string;
    isBulkPreview?: boolean;
  } = {},
): ColumnDef<Approval & EmissionsType> => {
  return {
    header: label || t('org-cfv-data-entry.docDate'), //單據日期（月 / 日 / 年）
    accessorKey: isBulkPreview ? 'documentDate' : 'activityData.documentDate',
    cell: ({ row: { original } }) => {
      const documentDate = isBulkPreview ? original.documentDate : original.activityData.documentDate;
      const documentEndDate = isBulkPreview ? original.documentEndDate : original.activityData.documentEndDate;

      return [formatDate(documentDate), formatDate(documentEndDate)].join(' - ');
    },
  };
};

export const getDocumentDateHeader = (
  t: NextTFunction,
  {
    showIconWhenIsBulk = true,
    label,
    isBulkPreview = false,
  }: {
    showIconWhenIsBulk?: boolean;
    label?: string;
    isBulkPreview?: boolean;
  },
): ColumnDef<Approval & EmissionsType> => {
  return {
    header: label || t('org-cfv-data-entry.docDate'),
    accessorKey: isBulkPreview ? 'documentDate' : 'activityData.documentDate',
    cell: ({ row: { original } }) => {
      const isBulkInput = !isBulkPreview && showIconWhenIsBulk && original.isBatch;
      const dateText = getDocumentDateText(original, isBulkPreview);

      return isBulkInput ? (
        <Flex alignItems='center' gap={2} data-testid={`bulk_input`}>
          {t('common.seeDetails')}
          <TooltipIcon />
        </Flex>
      ) : (
        dateText
      );
    },
  };
};

export const getDateHeader = (
  t: NextTFunction,
  {
    showIconWhenIsBulk = true,
    label,
    isBulkPreview = false,
  }: {
    showIconWhenIsBulk?: boolean;
    label?: string;
    isBulkPreview?: boolean;
  },
): ColumnDef<Approval & EmissionsType> => {
  return getDocumentDateHeader(t, {
    showIconWhenIsBulk,
    label: label || t('org-cfv-data-entry.date'),
    isBulkPreview,
  });
};

export const getConvertedUsageAmountDefaultHeader = (t: NextTFunction): ColumnDef<Approval & EmissionsType> => {
  return {
    header: t('org-cfv-data-entry.usageAmount'),
    accessorKey: 'activityData.metadata.convertedUsageAmount',
    cell: ({ getValue }) => toThousandthString(getValue() as number),
  };
};

export const getConvertedUsageAmountHeader = (
  t: NextTFunction,
  showSeeDetailsSetting: boolean = true,
  label?: string,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  return {
    header: label || t('org-cfv-data-entry.usageAmount'), //使用量
    accessorKey: isBulkPreview ? 'metadata.convertedUsageAmount' : 'activityData.metadata.convertedUsageAmount',
    cell: ({ row: { original }, getValue }) => {
      const isBulkInput = !isBulkPreview && showSeeDetailsSetting && original.isBatch;

      return isBulkInput ? t('common.seeDetails') : toThousandthString(getValue() as number); //見詳情
    },
  };
};

export const getConvertedUnitDefaultHeader = (t: NextTFunction): ColumnDef<Approval & EmissionsType> => {
  return {
    header: t('org-cfv-create-emission.unit'),
    accessorKey: 'activityData.metadata.convertedUnit',
    cell: ({ getValue }) => t(`unit.${getValue()}`),
  };
};

export const getConvertedUnitHeader = (
  t: NextTFunction,
  showSeeDetailsSetting: boolean = true,
  label?: string,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  return {
    header: label || t('org-cfv-create-emission.unit'),
    accessorKey: isBulkPreview ? 'metadata.convertedUnit' : 'activityData.metadata.convertedUnit',
    cell: ({ row: { original }, getValue }) => {
      const isBulkInput = !isBulkPreview && showSeeDetailsSetting && original.isBatch;

      return isBulkInput ? t('common.seeDetails') : t(`unit.${getValue()}`);
    },
  };
};

export const getMaskedConvertedUsageAmountHeader = (t: NextTFunction): ColumnDef<Approval & EmissionsType> => {
  return {
    header: t('org-cfv-data-entry.usageAmount'),
    accessorKey: 'activityData.metadata.convertedUsageAmount',
    cell: ({ row: { original } }) =>
      isNumber(original.metadata?.convertedUsageAmount)
        ? original.metadata?.convertedUsageAmount
        : t('common.seeDetails'),
  };
};

export const getMaskedConvertedUnitHeader = (t: NextTFunction): ColumnDef<Approval & EmissionsType> => {
  return {
    header: t('org-cfv-create-emission.unit'),
    accessorKey: 'activityData.metadata.convertedUnit',
    cell: ({ row: { original } }) =>
      original.activityData.metadata?.convertedUnit
        ? t(`unit.${original.activityData.metadata?.convertedUnit}`)
        : t('common.seeDetails'),
  };
};

export const getAttachmentHeader = (
  t: NextTFunction,
  onViewAttachmentClick: (file: Document) => void,
): ColumnDef<Approval & EmissionsType> => {
  return {
    header: t('common.supportingDocument'),
    accessorFn: (row) => {
      return get(row, 'activityData.documents.0.name') || get(row, 'documents.0.name');
    },
    cell: ({ row: { original }, getValue }) => {
      const fileName = getValue();
      return !isEmpty(fileName) ? (
        <Flex gap={2}>
          <Text>{getValue() as string}</Text>
          <Tooltip hasArrow label={t('common.view')}>
            <IconButton
              variant='link'
              color='blue.500'
              aria-label='view'
              fontSize='24px'
              icon={<ViewIcon color='black' />}
              onClick={
                original.activityData.documents
                  ? (e) => {
                      onViewAttachmentClick(original.activityData.documents?.[0] as unknown as Document);
                      e.preventDefault();
                      e.stopPropagation();
                      return false;
                    }
                  : undefined
              }
            />
          </Tooltip>
        </Flex>
      ) : (
        '-'
      );
    },
  };
};

export const getStatusHeader = (t: NextTFunction): ColumnDef<Approval & EmissionsType> => {
  return {
    header: t('common.status'),
    accessorKey: 'status',
    cell: ({ getValue }) => {
      return (
        <Badge
          data-testid={`status_${getValue()}`}
          colorScheme={ActivityDataConstants.STATUS_COLOR[getValue() as DataActivityStatus]}
        >
          <Text fontWeight='normal'>{t(`common.status-${getValue()}`)}</Text>
        </Badge>
      );
    },
  };
};

export const getUsageRatioHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.usageRatio' : 'activityData.metadata.usageRatio';
  return {
    header: t('org-cfv-data-entry.activityDataAllocationRatio'),
    accessorKey,
  };
};

export const getSubmittedNameHeader = (t: NextTFunction): ColumnDef<Approval & EmissionsType> => {
  return {
    header: t('common.submittedPerson'),
    accessorKey: 'submittedBy.name',
  };
};

export const getSubmittedDeptHeader = (
  t: NextTFunction,
  showBulkIcon: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  return {
    header: t('account-management.dept'),
    accessorKey: 'submittedBy.departmentName',
    cell: ({ getValue }) => {
      const department = (getValue() as string) || '-';
      return showBulkIcon && getValue() ? (
        <Flex alignItems='center' gap={2} data-testid={`bulk_input`}>
          {department}
          <TooltipIcon />
        </Flex>
      ) : (
        department
      );
    },
  };
};

export const getEmissionsHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'emission' : 'activityData.emission';
  return {
    header: t('org-cfv-data-entry.emissions'),
    accessorKey,
    cell: ({ getValue }) => transferEmissionsDisplay(t, { value: getValue() as number, isOnlyValue: true }),
  };
};

export const getEmissionsUnitHeader = (
  t: NextTFunction,
  {
    label,
    isBulkPreview = false,
  }: {
    label?: string;
    isBulkPreview?: boolean;
  } = {},
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'emissionUnit' : 'activityData.emissionUnit';
  return {
    header: label || t('org-cfv-create-emission.unit'),
    accessorKey,
    cell: ({ getValue }) =>
      getValue() ? `${t(`unit.${(getValue() as string).replace('CO2E', '')}`)}${t('ghg.CO2E')}` : '-',
  };
};

export const getEmissionsWithUnitHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  return {
    header: t('org-cfv-data-entry.emissions'),
    accessorKey: isBulkPreview ? 'emission' : 'activityData.emission',
    cell: ({ row: { original }, getValue }) => {
      const emission = toThousandthString(getValue() as number);
      const unit = isBulkPreview ? original.emissionUnit : original.activityData.emissionUnit;
      const unitLabel = `${t(`unit.${((unit as string) ?? '').replace('CO2E', '')}`)}${t('ghg.CO2E')}`;
      return `${emission} ${unitLabel}`;
    },
  };
};

export const getDocumentNumberHeader = (
  t: NextTFunction,
  {
    showIconWhenIsBulk = true,
    label,
    isBulkPreview = false,
  }: {
    showIconWhenIsBulk?: boolean;
    label?: string;
    isBulkPreview?: boolean;
  },
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'documentNumber' : 'activityData.documentNumber';
  return {
    header: label || t('org-cfv-data-entry.docNo'),
    accessorKey,
    cell: ({ getValue, row: { original } }) => {
      const isBulkInput = !isBulkPreview && showIconWhenIsBulk && original.isBatch;

      return isBulkInput ? (
        <Flex alignItems='center' gap={2} data-testid={`bulk_input`}>
          {t('common.seeDetails')}
          <TooltipIcon />
        </Flex>
      ) : (
        getValue()
      );
    },
  };
};

export const getUsageAmountHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.usageAmount' : 'activityData.metadata.usageAmount';
  return {
    header: t('org-cfv-data-entry.usageAmount'),
    accessorKey,
  };
};

export const getUnitHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.unit' : 'activityData.metadata.unit';
  return {
    header: t('org-cfv-data-entry.activityDataUnit'),
    accessorKey,
    cell: ({ getValue }) => t(`unit.${getValue()}`),
  };
};

export const getAmountHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.amount' : 'activityData.metadata.amount';
  return {
    header: t('org-cfv-data-entry.expense'),
    accessorKey,
    cell: ({ getValue }) => toThousandthString(getValue() as number),
  };
};

export const getCurrencyUnitHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.currency' : 'activityData.metadata.currency';
  return {
    header: t('common.currencyUnit'),
    accessorKey,
    cell: ({ getValue }) => t(`currency.${getValue()}`),
  };
};

export const getActivityDataTypeHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.dataType' : 'activityData.metadata.dataType';
  return {
    header: t('org-cfv-data-entry.activityDataType'),
    accessorKey,
    cell: ({ getValue }) => {
      const val = getValue();
      return val ? t(`org-cfv-data-entry.activityDataType-${val}`) : '';
    },
  };
};

export const getDataConfidenceTypeHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.dataConfidenceType' : 'activityData.metadata.dataConfidenceType';
  return {
    header: t('org-cfv-data-entry.activityDataConfidenceType'),
    accessorKey,
    cell: ({ getValue }) => {
      const val = getValue();
      return val ? t(`org-cfv-data-entry.${val}`) : '';
    },
  };
};

export const getCommentHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  return {
    header: t('common.comment'),
    accessorKey: isBulkPreview ? 'metadata.comment' : 'activityData.metadata.comment',
  };
};

export const getUsageCountHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.usageCount' : 'activityData.metadata.usageCount';
  return {
    header: t('org-cfv-data-entry.usageCount'),
    accessorKey,
    cell: ({ getValue }) => toThousandthString(getValue() as number),
  };
};

export const getTransportationRouteHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.transportationRoute' : 'activityData.metadata.transportationRoute';
  return {
    header: t('fd-rmt.transportationRoute'),
    accessorKey,
    cell: ({ getValue }) => {
      const val = getValue();

      switch (val) {
        case RMTRouteType.DEFAULT:
          return t('fd-rmt.defaultRoute');
        case RMTRouteType.NONE:
          return t('fd-rmt.noTransportPath');
        default:
          return '';
      }
    },
  };
};

export const getSourceHeader = (t: NextTFunction): ColumnDef<Approval & EmissionsType> => {
  return {
    header: t('bulk-input.dataSource'),
    accessorKey: 'metadata.source',
    cell: ({ row: { original }, getValue }) => {
      const val = getValue();

      return val === RawMaterialDataSourceType.OTHER ? original?.metadata?.sourceOther : val;
    },
  };
};

export const getFactorErrorTypeHeader = (
  t: NextTFunction,
  {
    fieldName,
    isBulkPreview = false,
  }: {
    fieldName?: string;
    isBulkPreview?: boolean;
  } = {},
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = fieldName
    ? fieldName
    : isBulkPreview
    ? 'metadata.factorErrorType'
    : 'activityData.metadata.factorErrorType';
  return {
    header: t('org-cfv-create-emission.emissionFactorType'),
    accessorKey,
    cell: ({ getValue }) => {
      const val = getValue() as string;
      return val ? t(`org-cfv-create-emission.factorErrorType-${val}`) : '';
    },
  };
};

export const getNameHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.name' : 'activityData.metadata.name';
  return {
    header: t('common.name'),
    accessorKey,
  };
};

export const getSepticTankWorkDays = (t: NextTFunction): ColumnDef<Approval & EmissionsType> => {
  return {
    header: t('org-cfv-data-entry.workDay'),
    accessorKey: 'metadata.septicTankActivity.workDays',
  };
};

export const getSepticTankAverageEmployees = (t: NextTFunction): ColumnDef<Approval & EmissionsType> => {
  return {
    header: t('org-cfv-data-entry.avgEmpNum'),
    accessorKey: 'metadata.septicTankActivity.averageEmployees',
  };
};

export const getSepticTankDailyWorkHours = (t: NextTFunction): ColumnDef<Approval & EmissionsType> => {
  return {
    header: t('org-cfv-data-entry.workingHoursPerDay'),
    accessorKey: 'metadata.septicTankActivity.dailyWorkHours',
  };
};

export const getNameOfEmissionFactorHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.factor.factorName' : 'activityData.metadata.factor.factorName';
  return {
    header: t('fd-raw-material.factorName'),
    accessorKey,
  };
};

export const getEmissionFactorHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.factor.factor' : 'activityData.metadata.factor.factor';
  return {
    header: t('org-cfv-create-emission.emissionFactors'),
    accessorKey,
    cell: ({ getValue }) => toThousandthString(toNumber(getValue())),
  };
};

export const getEmissionFactorUnitHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.factor.ghgUnit' : 'activityData.metadata.factor.ghgUnit';
  return {
    header: t('fd-raw-material.emissionFactorUnit'),
    accessorKey,
    cell: ({ row: { original } }) => {
      const { ghgUnit, factorUnit } = original?.metadata?.factor;
      if (!ghgUnit || !factorUnit) {
        return '';
      }

      const ghtUnitWording = t(`unit.${ghgUnit}`);
      const unitType = t('ghg.CO2E');
      const factorUnitWording = t(`unit.${factorUnit}`);

      return `${ghtUnitWording}${unitType}/${factorUnitWording}`;
    },
  };
};

export const getSourceOfEmissionHeader = (
  t: NextTFunction,
  {
    label,
    isBulkPreview = false,
  }: {
    label?: string;
    isBulkPreview?: boolean;
  },
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.factor.factorSource' : 'activityData.metadata.factor.factorSource';
  return {
    header: label || t('fd-rmt.factorSource'),
    accessorKey,
    cell: ({ getValue }) => {
      const val = getValue() as string;
      try {
        return safeTranslate(t, `factor-source.${val}`, val ?? '');
      } catch {
        return val;
      }
    },
  };
};

export const getActivationDateHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.factor.activationDate' : 'activityData.metadata.factor.activationDate';
  return {
    header: t('bulk-input-template.activationDate'),
    accessorKey,
    cell: ({ getValue }) => {
      const val = getValue() as Date;
      return isValidDate(val) ? dayjs(val).format(APP_DATE_FORMAT) : '-';
    },
  };
};

export const getVisitorHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.visitorCount' : 'activityData.metadata.visitorCount';
  return {
    header: t('common.visitor'),
    accessorKey,
  };
};

export const getVisitCountHeader = (
  t: NextTFunction,
  isBulkPreview: boolean = false,
): ColumnDef<Approval & EmissionsType> => {
  const accessorKey = isBulkPreview ? 'metadata.totalVisits' : 'activityData.metadata.totalVisits';
  return {
    header: t('common.visitCount'),
    accessorKey,
  };
};
